// `/app/splits` (WEB-123, `spec/19` §5.2 e §5.3): Recebidos, Enviados e Autorizações.
//
// Rota-PAI com duas filhas de papéis diferentes:
// - `$id` é o detalhe em drawer (`spec/24` §1 decisão 5): a lista continua desenhada
//   embaixo e o `RouteDrawer` mostra o `<Outlet/>` por cima.
// - `authorizations` é uma ABA: quando ela casa, o `<Outlet/>` vira o conteúdo do painel e
//   não há lista nem drawer.
//
// `tab`, `status` e `period` vivem na query string (`spec/17` §3); o schema fica aqui porque o
// `$id` herda o mesmo `search`, e é pelo `tab` que o drawer sabe de que ponta o seller está.
// Listas e KPIs respeitam o toggle Produção/Teste (`mode` na query key e no `X-Mode`).

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { UserPlus } from 'lucide-react';
import { z } from 'zod';
import {
  type SplitRole,
  splitQuotaQueryOptions,
  splitsQueryOptions,
  splitWindowQueryOptions,
} from '../api/queries/splits.ts';
import { startOfDayInSaoPaulo } from '../api/queries/transactions.ts';
import { useMode } from '../app/mode.tsx';
import { RouteDrawer } from '../components/app/route-drawer.tsx';
import { PiaoLink, TransactionLink } from '../features/splits/links.tsx';
import { SPLIT_STATUSES } from '../features/splits/model.ts';
import { PERIODS, type Period, SplitsList } from '../features/splits/splits-list.tsx';
import { type Loadable, SplitsScreen, type SplitsTab } from '../features/splits/splits-screen.tsx';
import { addDaysToIsoDate, todayIsoDate } from '../lib/dates.ts';

const searchSchema = z.object({
  tab: z.enum(['paid']).optional().catch(undefined),
  status: z.enum(SPLIT_STATUSES).optional().catch(undefined),
  period: z.enum(PERIODS).optional().catch(undefined),
});

const PERIOD_DAYS: Record<Period, number> = { '7d': 7, '30d': 30, '90d': 90 };

/**
 * Início do período no fuso de São Paulo, arredondado ao DIA: o valor entra na query key, e
 * um instante recalculado a cada render mudaria a key a cada render (refetch sem fim).
 */
function periodStart(period: Period | undefined): string | undefined {
  if (period === undefined) {
    return undefined;
  }
  return startOfDayInSaoPaulo(addDaysToIsoDate(todayIsoDate(), -PERIOD_DAYS[period]));
}

function loadable<T>(query: {
  data: T | undefined;
  isPending: boolean;
  error: unknown;
  refetch: () => unknown;
}): Loadable<T> {
  return {
    data: query.data,
    isLoading: query.isPending,
    error: query.error,
    onRetry: () => {
      void query.refetch();
    },
  };
}

function Splits() {
  const { mode } = useMode();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const role: SplitRole = search.tab === 'paid' ? 'paid' : 'received';

  const authorizations = useMatch({ from: '/app/splits/authorizations', shouldThrow: false });
  const detail = useMatch({ from: '/app/splits/$id', shouldThrow: false });
  const tab: SplitsTab = authorizations === undefined ? role : 'authorizations';

  const received = useQuery(splitWindowQueryOptions(mode, 'received'));
  const paid = useQuery(splitWindowQueryOptions(mode, 'paid'));
  const quota = useQuery(splitQuotaQueryOptions(mode));

  const list = useInfiniteQuery({
    ...splitsQueryOptions(mode, {
      role,
      status: search.status,
      createdFrom: periodStart(search.period),
    }),
    enabled: tab !== 'authorizations',
  });
  const rows = (list.data?.pages ?? []).flatMap((page) => page.data);

  const listSearch = (next: {
    tab?: 'paid' | undefined;
    status?: (typeof SPLIT_STATUSES)[number] | undefined;
    period?: Period | undefined;
  }) => ({
    ...(next.tab === undefined ? {} : { tab: next.tab }),
    ...(next.status === undefined ? {} : { status: next.status }),
    ...(next.period === undefined ? {} : { period: next.period }),
  });

  return (
    <>
      <SplitsScreen
        mode={mode}
        tab={tab}
        onTabChange={(next) => {
          if (next === 'authorizations') {
            void navigate({ to: '/app/splits/authorizations' });
            return;
          }
          // Trocar de ponta limpa os filtros: status e período de uma aba não significam o
          // mesmo recorte na outra.
          void navigate({ to: '/app/splits', search: next === 'paid' ? { tab: 'paid' } : {} });
        }}
        received={loadable(received)}
        paid={loadable(paid)}
        quota={loadable(quota)}
      >
        {tab === 'authorizations' ? (
          <Outlet />
        ) : (
          <SplitsList
            side={role}
            rows={rows}
            isLoading={list.isPending}
            error={list.error}
            onRetry={() => {
              void list.refetch();
            }}
            filters={{ status: search.status, period: search.period }}
            onFiltersChange={(next) => {
              void navigate({
                to: '/app/splits',
                search: listSearch({ tab: search.tab, ...next }),
                replace: true,
              });
            }}
            hasMore={list.hasNextPage}
            isLoadingMore={list.isFetchingNextPage}
            onLoadMore={() => {
              void list.fetchNextPage();
            }}
            onSelect={(split) => {
              void navigate({ to: '/app/splits/$id', params: { id: split.id }, search });
            }}
            selectedId={detail?.params.id}
            emptyAction={
              role === 'received' ? (
                <PiaoLink
                  to="/app/splits/authorizations"
                  label="Autorizar um e-mail"
                  icon={UserPlus}
                />
              ) : (
                <PiaoLink to="/app/transactions" label="Ir para Transações" />
              )
            }
            {...(role === 'paid'
              ? { transactionLink: (id: string) => <TransactionLink id={id} /> }
              : {})}
          />
        )}
      </SplitsScreen>

      {tab !== 'authorizations' && (
        <RouteDrawer
          open={detail !== undefined}
          onClose={() => {
            void navigate({ to: '/app/splits', search });
          }}
          title={role === 'paid' ? 'Split enviado' : 'Split recebido'}
          {...(detail === undefined
            ? {}
            : { sub: <span className="font-mono">{detail.params.id}</span> })}
        >
          <Outlet />
        </RouteDrawer>
      )}
    </>
  );
}

export const Route = createFileRoute('/app/splits')({
  validateSearch: searchSchema,
  component: Splits,
});
